import { Link } from "react-router-dom";
import { ArrowRight, Sparkle } from "@phosphor-icons/react";
import ShareButton from "@/components/ShareButton";

/**
 * FeelThePulseIntro — a poetic intro card shown at the top of /pulse.
 * Links to the full editorial article at /pulse/story.
 */
export default function FeelThePulseIntro() {
  return (
    <section
      data-testid="feel-the-pulse-intro"
      className="max-w-[1400px] mx-auto px-5 md:px-10 mb-6"
    >
      <div className="rounded-2xl overflow-hidden border border-black/10 bg-gradient-to-br from-[#1B4332] via-[#1E1A16] to-[#241A10] text-[#F5EBDC] shadow-sm">
        <div className="grid md:grid-cols-[1.6fr_1fr] gap-0">
          {/* Left column — copy */}
          <div className="p-6 md:p-10">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#E4A15C] mb-2 inline-flex items-center gap-2">
              <Sparkle size={11} weight="fill" />
              Feel the Pulse of Nature
            </div>
            <h2 className="font-serif-display text-3xl md:text-5xl leading-[1.03] tracking-tight">
              Nature is always changing.
            </h2>
            <p className="mt-4 text-[#E5D8C4]/85 text-sm md:text-base leading-relaxed max-w-2xl">
              A tree begins to flower. A flock of birds arrives earlier than expected. Butterflies appear in a garden. A river rises after heavy rain. A familiar pond begins to dry. A shoreline slowly retreats. A new animal is spotted in a neighborhood. An unusually hot afternoon becomes part of a longer heat wave.
            </p>
            <p className="mt-3 text-[#E5D8C4]/85 text-sm md:text-base">
              Individually, these moments may seem small.
            </p>
            <p className="mt-1 font-serif-display text-lg md:text-xl text-[#F5EBDC]">
              Together, they are the <span className="text-[#E4A15C]">pulse of nature</span>.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to="/pulse/story"
                data-testid="feel-the-pulse-explore-more"
                className="inline-flex items-center gap-2 rounded-full bg-[#E4A15C] text-[#1B4332] px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase hover:bg-[#F5EBDC] transition-colors"
              >
                Explore more <ArrowRight size={12} weight="bold" />
              </Link>
              <ShareButton
                title="Feel the Pulse of Nature — NaturePulse"
                text="A living record of our changing planet, told by the community."
                hashtags={["NaturePulse", "FeelThePulse"]}
                variant="chip"
                testid="feel-the-pulse-share"
              />
            </div>
          </div>

          {/* Right column — icon collage suggesting what a Pulse can be */}
          <div className="hidden md:flex bg-[#0E0A06] border-l border-white/5 items-center justify-center p-6">
            <div className="grid grid-cols-3 gap-3 text-2xl leading-none opacity-90 select-none" aria-hidden="true">
              {["🌱", "🐦", "🐝", "🌳", "🐘", "🐟", "🌊", "🔥", "💧", "⛈️", "🌸", "🔬"].map((e) => (
                <div key={e} className="w-12 h-12 rounded-xl bg-white/5 border border-white/5 grid place-items-center">
                  {e}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
